import { initializeApp } from 'firebase/app'
import { getAuth, signInAnonymously } from 'firebase/auth'
import { getDatabase } from 'firebase/database'
import { getStorage } from 'firebase/storage'
import { getAnalytics } from "firebase/analytics"
import { getPerformance } from "firebase/performance";

import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  deleteToken
} from 'firebase/messaging'
import emitter from './event-bus'
import { storageAvailable } from './utils/safeStorage'

const firebaseConfig = {
  apiKey: process.env.VUE_APP_API_KEY,
  authDomain: process.env.VUE_APP_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_DATABASE_URL,
  projectId: process.env.VUE_APP_PROJECT_ID,
  storageBucket: process.env.VUE_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_APP_ID,
  measurementId: process.env.VUE_APP_MEASUREMENT_ID
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

// TB-071: optional SDKs must never stop the portal from mounting. With site
// storage blocked, Performance throws during init, so it is skipped; any other
// SDK that fails is left null. One warning covers the whole start-up.
const skipped = []
function optional (name, init) {
  try {
    return init()
  } catch (e) {
    skipped.push(name)
    return null
  }
}
const canStore = storageAvailable()
if (!canStore) skipped.push('performance')
const analytics = optional('analytics', () => getAnalytics(app))
const performance = canStore ? optional('performance', () => getPerformance(app)) : null
const auth = optional('auth', () => getAuth(app))
const database = optional('database', () => getDatabase(app))
const storage = optional('storage', () => getStorage(app))

// Sign in anonymously. Realtime Database rules need the auth session, so chat
// listeners wait on authReady: on a direct load or reload of /chat the
// subscription would otherwise start before sign-in and be denied.
const authReady = auth
  ? signInAnonymously(auth).then((c) => c?.user || null).catch(() => null)
  : Promise.resolve(null)
if (skipped.length) {
  console.warn('[TB-071] Site storage blocked or Firebase unavailable; running without:', skipped.join(', '))
}

export { app, database, storage, analytics,performance, authReady }

// TB-086 (DEF-235): `prompt: false` (app start-up) only refreshes the token when permission
// was already granted. Asking without a user gesture is refused by Safari (page error) and
// prompts every new visitor on the landing page in other browsers.
export async function manageNotifications({ prompt = true } = {}) {
  try {
    // 0. Ensure Messaging is supported in this environment (HTTPS, supported browser)
    const messagingSupported = await isSupported().catch(() => false)
    if (!messagingSupported) {
      console.warn(
        'Firebase Messaging is not supported in this browser/environment.'
      )
      return null
    }

    // 1. Check permission
    if (Notification.permission === 'default') {
      if (!prompt) return null
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        console.warn('User denied notification permission.')
        return null
      }
    } else if (Notification.permission === 'denied') {
      console.warn(
        'Notifications are blocked. User must enable in browser settings.'
      )
      return null
    }

    // 2. Only now try to get Firebase token
    const messaging = getMessaging(app)
    // 2a. Register service worker explicitly and use it when fetching token
    let serviceWorkerRegistration = undefined
    if ('serviceWorker' in navigator) {
      try {
        // The service worker file must be located at public/firebase-messaging-sw.js
        serviceWorkerRegistration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js'
        )
        // Ensure the SW is ready before requesting the token
        await navigator.serviceWorker.ready
      } catch (swErr) {
        console.warn('Service worker registration failed:', swErr)
      }
    }
    const currentToken = await getToken(messaging, {
      vapidKey: process.env.VUE_APP_WEB_PUSH,
      serviceWorkerRegistration
    })

    if (currentToken) {
      localStorage.setItem('webToken', currentToken)
      return currentToken
    } else {
      console.warn('No registration token available.')
      return null
    }
  } catch (error) {
    console.error('Error in manageNotifications:', error)
    return null
  }
}

// Listen for foreground messages to verify payloads while the page is open
export function attachForegroundMessageLogger() {
  isSupported().then((supported) => {
    if (!supported) return
    const messaging = getMessaging(app)
    onMessage(messaging, (payload) => {
      console.log('FCM foreground message:', payload)
      try {
        emitter.emit('fcm-message', payload)
      } catch {}
      // Optional: show a simple notification using the Notifications API
      try {
        if (Notification.permission === 'granted') {
          const title =
            payload?.notification?.title || payload?.data?.title || ''
          const body =
            payload?.notification?.body ||
            payload?.data?.message ||
            payload?.data?.body ||
            ''
          if (title || body) {
            const n = new Notification(title || 'Notification', {
              body
            })
            // A patient's own (medication) reminder opens My Reminders.
            if (payload?.data?.eventType === 'patient.reminder') {
              n.onclick = () => {
                window.focus()
                import('@/router').then((m) => m.default.push({ name: 'reminders', query: { tab: 'mine' } })).catch(() => {})
                n.close()
              }
            }
          }
        }
      } catch {}
    })
  })
}

// Force refresh the FCM token to avoid using a stale or mismatched token
export async function forceRefreshMessagingToken() {
  try {
    const supported = await isSupported().catch(() => false)
    if (!supported) return null
    const messaging = getMessaging(app)
    try {
      await deleteToken(messaging)
    } catch (e) {
      console.warn('deleteToken failed or no existing token:', e)
    }
    let serviceWorkerRegistration = undefined
    if ('serviceWorker' in navigator) {
      try {
        serviceWorkerRegistration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js'
        )
        await navigator.serviceWorker.ready
      } catch {}
    }
    const newToken = await getToken(messaging, {
      vapidKey: process.env.VUE_APP_WEB_PUSH,
      serviceWorkerRegistration
    })
    if (newToken) {
      localStorage.setItem('webToken', newToken)
    }
    return newToken ?? null
  } catch (err) {
    console.error('forceRefreshMessagingToken error:', err)
    return null
  }
}

// export function manageNotifications() {
//   // Manage Notifications
//   try {
//     const messaging = getMessaging(app);
//     getToken(messaging, { vapidKey: process.env.VUE_APP_WEB_PUSH })
//       .then(currentToken => {
//         if (currentToken) {
//           localStorage.setItem("webToken", currentToken);
//         } else {
//           alert("Please! Allow notification permission.");
//         }
//       })
//       .catch(err => {
//         console.log("An error occurred while retrieving token. ", err);
//       });
//   } catch (error) {
//     console.log("Messaging not supported in this environment:", error);
//   }
// }
